package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import se.mittpass.shared.api.BillingStatusResponse

private val billingScope = MainScope()

@Composable
fun BillingPage(workplaceId: Long? = null) {
    var status by remember { mutableStateOf<BillingStatusResponse?>(null) }
    var usageHistory by remember { mutableStateOf<List<Map<String, String>>>(emptyList()) }
    var invoices by remember { mutableStateOf<List<Map<String, String>>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var actionLoading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(workplaceId) {
        val q = if (workplaceId != null) "?workplaceId=$workplaceId" else ""
        try { status = httpClient.get("billing/status$q").body() } catch (_: Exception) {}
        try { usageHistory = httpClient.get("billing/usage-history$q").body() } catch (_: Exception) {}
        try { invoices = httpClient.get("billing/invoices$q").body() } catch (_: Exception) {}
        loading = false
    }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        // Header
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
            H3({ style { property("margin", "0 0 0.25rem 0"); property("font-size", "1.3rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                Text("Prenumeration")
            }
            P({ style { property("margin", "0"); property("color", "var(--rp-muted)"); property("font-size", "0.85rem") } }) {
                Text("Hantera din prenumeration och se faktureringshistorik")
            }
        }

        message?.let { msg ->
            val isSuccess = msg.startsWith("✓")
            Div({
                style {
                    property("background", if (isSuccess) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                    property("color", if (isSuccess) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                    property("padding", "0.75rem 1.25rem"); property("border-radius", "14px"); property("font-weight", "600")
                }
            }) { Text(msg) }
        }

        if (loading) {
            Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-subtle)") } }) { Text("Laddar…") }
            return@Div
        }

        val s = status ?: run {
            Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-err-text)") } }) {
                Text("Kunde inte ladda prenumerationsinformation.")
            }
            return@Div
        }

        // ── Current status ──
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
            H4({ style { property("margin", "0 0 1rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Nuvarande status") }

            val (statusLabel, statusBg, statusFg) = when (s.planStatus) {
                "trial" -> Triple(
                    if ((s.daysLeftInTrial ?: 0) > 0) "Provperiod — ${s.daysLeftInTrial} dagar kvar" else "Provperiod avslutad",
                    if ((s.daysLeftInTrial ?: 0) > 0) "var(--rp-info-bg)" else "var(--rp-err-bg)",
                    if ((s.daysLeftInTrial ?: 0) > 0) "var(--rp-iris)" else "var(--rp-err-text)"
                )
                "active" -> Triple("Aktiv prenumeration", "var(--rp-ok-bg)", "var(--rp-ok-text)")
                "past_due" -> Triple("Betalning förfallen", "var(--rp-err-bg)", "var(--rp-err-text)")
                "canceled" -> Triple("Avslutad", "var(--rp-overlay)", "var(--rp-subtle)")
                else -> Triple(s.planStatus, "var(--rp-overlay)", "var(--rp-subtle)")
            }

            Div({
                style {
                    property("display", "inline-block"); property("padding", "0.4rem 1rem")
                    property("background", statusBg); property("color", statusFg)
                    property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.85rem")
                    property("margin-bottom", "1.25rem")
                }
            }) { Text(statusLabel) }

            Div({
                style {
                    property("display", "grid"); property("grid-template-columns", "repeat(3, 1fr)")
                    property("gap", "1rem"); property("margin-bottom", "1.25rem")
                }
            }) {
                BillingStatCard("Aktiva användare", "${s.activeUsersThisMonth}", "denna månad")
                BillingStatCard("Pris per användare", "39 kr", "exkl. moms")
                BillingStatCard("Beräknad faktura", "${s.estimatedInvoiceSek.toLong()} kr", "nästa månad")
            }

            P({ style { property("font-size", "0.78rem"); property("color", "var(--rp-muted)"); property("margin", "0 0 1.25rem 0") } }) {
                Text("Aktiv användare = chef (alltid) eller personal med minst en incheckning under månaden. En användare som var aktiv under en månad räknas alltid för den månadens faktura, oavsett om de senare avaktiverats.")
            }

            if (!s.stripeEnabled) {
                Div({ style { property("padding", "0.75rem 1rem"); property("background", "var(--rp-warn-bg)"); property("border-radius", "12px"); property("font-size", "0.85rem"); property("color", "var(--rp-gold)") } }) {
                    Text("Stripe är inte konfigurerat — kontakta support@mittpass.se för att aktivera betalning.")
                }
                return@Div
            }

            Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("flex-wrap", "wrap") } }) {
                if (!s.hasSubscription) {
                    Button({
                        onClick {
                            actionLoading = true
                            billingScope.launch {
                                try {
                                    val q = if (workplaceId != null) "?workplaceId=$workplaceId" else ""
                                    val resp: Map<String, String> = httpClient.post("billing/checkout$q").body()
                                    val url = resp["checkoutUrl"]
                                    if (url != null) kotlinx.browser.window.location.href = url
                                } catch (_: Exception) { message = "Kunde inte starta betalningsflödet. Försök igen." }
                                actionLoading = false
                            }
                        }
                        if (actionLoading) disabled()
                        style {
                            property("padding", "0.75rem 1.5rem"); property("border", "none")
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white")
                            property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.9rem"); property("cursor", "pointer")
                        }
                    }) { Text(if (actionLoading) "Laddar…" else "Starta prenumeration") }
                } else {
                    Button({
                        onClick {
                            actionLoading = true
                            billingScope.launch {
                                try {
                                    val q = if (workplaceId != null) "?workplaceId=$workplaceId" else ""
                                    val resp: Map<String, String> = httpClient.post("billing/portal$q").body()
                                    val url = resp["portalUrl"]
                                    if (url != null) kotlinx.browser.window.location.href = url
                                } catch (_: Exception) { message = "Kunde inte öppna faktureringsinställningar." }
                                actionLoading = false
                            }
                        }
                        if (actionLoading) disabled()
                        style {
                            property("padding", "0.75rem 1.5rem"); property("border", "2px solid var(--rp-pine)")
                            property("background", "transparent"); property("color", "var(--rp-pine)")
                            property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.9rem"); property("cursor", "pointer")
                        }
                    }) { Text(if (actionLoading) "Laddar…" else "Hantera prenumeration") }

                    Button({
                        onClick {
                            if (kotlinx.browser.window.confirm("Är du säker på att du vill avsluta prenumerationen? Den avslutas omedelbart.")) {
                                actionLoading = true
                                billingScope.launch {
                                    try {
                                        val q = if (workplaceId != null) "?workplaceId=$workplaceId" else ""
                                        httpClient.post("billing/cancel$q")
                                        message = "Prenumerationen har avslutats."
                                        // Reload status
                                        val statusResp: BillingStatusResponse = httpClient.get("billing/status$q").body()
                                        status = statusResp
                                    } catch (_: Exception) { message = "Kunde inte avsluta prenumerationen." }
                                    actionLoading = false
                                }
                            }
                        }
                        if (actionLoading) disabled()
                        style {
                            property("padding", "0.75rem 1.5rem"); property("border", "2px solid var(--rp-err-border)")
                            property("background", "transparent"); property("color", "var(--rp-love)")
                            property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.9rem"); property("cursor", "pointer")
                            property("margin-left", "0.5rem")
                        }
                    }) { Text(if (actionLoading) "Laddar…" else "Avsluta prenumeration") }
                }
            }
        }

        // ── Usage history per month ──
        if (usageHistory.isNotEmpty()) {
            Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
                H4({ style { property("margin", "0 0 1rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Användarhistorik (senaste 6 månaderna)") }
                P({ style { property("font-size", "0.78rem"); property("color", "var(--rp-muted)"); property("margin", "0 0 1rem 0") } }) {
                    Text("Visar antal aktiva användare per månad. Användare räknas baserat på faktisk aktivitet den månaden.")
                }
                Div({
                    style {
                        property("display", "grid"); property("grid-template-columns", "repeat(auto-fill, minmax(140px, 1fr))")
                        property("gap", "0.75rem")
                    }
                }) {
                    usageHistory.forEach { row ->
                        val month = row["month"]?.toIntOrNull() ?: 0
                        val year = row["year"] ?: ""
                        val users = row["activeUsers"] ?: "0"
                        val price = row["priceSek"] ?: "0"
                        val monthName = if (month in 1..12) MONTH_NAMES[month - 1] else ""
                        Div({
                            style {
                                property("background", "var(--rp-overlay)"); property("border-radius", "16px"); property("padding", "0.875rem")
                                property("text-align", "center")
                            }
                        }) {
                            Div({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-muted)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.5rem") } }) {
                                Text("$monthName $year")
                            }
                            Div({ style { property("font-size", "1.6rem"); property("font-weight", "900"); property("color", "var(--rp-pine)"); property("line-height", "1") } }) { Text(users) }
                            Div({ style { property("font-size", "0.7rem"); property("color", "var(--rp-muted)"); property("margin-top", "0.25rem") } }) { Text("användare") }
                            Div({ style { property("font-size", "0.8rem"); property("font-weight", "700"); property("color", "var(--rp-text)"); property("margin-top", "0.35rem") } }) { Text("$price kr") }
                        }
                    }
                }
            }
        }

        // ── Invoice history ──
        if (invoices.isNotEmpty()) {
            Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
                H4({ style { property("margin", "0 0 1rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Fakturahistorik") }
                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.5rem") } }) {
                    invoices.forEach { inv ->
                        val status2 = inv["status"] ?: ""
                        val (statusLabel2, statusBg2, statusFg2) = when (status2) {
                            "paid" -> Triple("Betald", "var(--rp-ok-bg)", "var(--rp-ok-text)")
                            "open" -> Triple("Öppen", "var(--rp-info-bg)", "var(--rp-pine)")
                            "uncollectible", "void" -> Triple("Ogiltig", "var(--rp-overlay)", "var(--rp-subtle)")
                            else -> Triple(status2, "var(--rp-overlay)", "var(--rp-subtle)")
                        }
                        val periodStart = inv["periodStart"]?.toLongOrNull()
                        val periodEnd = inv["periodEnd"]?.toLongOrNull()
                        val created = inv["created"]?.toLongOrNull()
                        val dateStr = if (created != null) epochToDateStr(created * 1000) else ""
                        val periodStr = if (periodStart != null && periodEnd != null)
                            "${epochToMonthStr(periodStart * 1000)} – ${epochToMonthStr(periodEnd * 1000)}"
                        else ""

                        Div({
                            style {
                                property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                                property("padding", "0.75rem 1rem"); property("background", "var(--rp-overlay)"); property("border-radius", "14px")
                                property("flex-wrap", "wrap"); property("gap", "0.5rem")
                            }
                        }) {
                            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.2rem") } }) {
                                Div({ style { property("font-weight", "700"); property("font-size", "0.9rem"); property("color", "var(--rp-text)") } }) {
                                    Text(if (periodStr.isNotBlank()) periodStr else dateStr)
                                }
                                if (inv["number"]?.isNotBlank() == true) {
                                    Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-muted)") } }) { Text("Faktura ${inv["number"]}") }
                                }
                            }
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
                                Div({ style { property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) {
                                    Text("${inv["amountPaid"]} ${inv["currency"] ?: "SEK"}")
                                }
                                Div({
                                    style {
                                        property("padding", "0.2rem 0.75rem"); property("background", statusBg2); property("color", statusFg2)
                                        property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.75rem")
                                    }
                                }) { Text(statusLabel2) }
                                val invoiceUrl = inv["hostedInvoiceUrl"]
                                if (!invoiceUrl.isNullOrBlank()) {
                                    A(attrs = {
                                        href(invoiceUrl)
                                        attr("target", "_blank")
                                        style {
                                            property("font-size", "0.8rem"); property("color", "var(--rp-pine)"); property("font-weight", "600")
                                            property("text-decoration", "none")
                                        }
                                    }) { Text("Visa ↗") }
                                }
                            }
                        }
                    }
                }
            }
        }

        // ── Pricing info ──
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
            H4({ style { property("margin", "0 0 0.75rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Prismodell") }
            listOf(
                "39 kr per aktiv användare och månad (exkl. moms)",
                "30 dagars gratis provperiod — inget kreditkort krävs",
                "Ingen startavgift och ingen bindningstid",
                "Inaktiva användare kostar ingenting",
                "Faktura skickas månadsvis i efterskott"
            ).forEach { item ->
                Div({ style { property("display", "flex"); property("gap", "0.5rem"); property("margin-bottom", "0.5rem"); property("font-size", "0.9rem"); property("color", "var(--rp-text)") } }) { Text(item) }
            }
        }
    }
}

private fun epochToDateStr(epochMs: Long): String {
    val d = js("new Date(epochMs)")
    val monthIdx = (d.getMonth() as Int)
    val month = MONTH_NAMES.getOrNull(monthIdx) ?: ""
    return "${d.getDate() as Int} $month ${d.getFullYear() as Int}"
}

private fun epochToMonthStr(epochMs: Long): String {
    val d = js("new Date(epochMs)")
    val monthIdx = (d.getMonth() as Int)
    val month = MONTH_NAMES.getOrNull(monthIdx) ?: ""
    return "$month ${d.getFullYear() as Int}"
}

@Composable
private fun BillingStatCard(label: String, value: String, sub: String) {
    Div({
        style {
            property("background", "var(--rp-overlay)"); property("border-radius", "16px"); property("padding", "1rem")
            property("text-align", "center")
        }
    }) {
        Div({ style { property("font-size", "1.5rem"); property("font-weight", "900"); property("color", "var(--rp-pine)"); property("margin-bottom", "0.25rem") } }) { Text(value) }
        Div({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px") } }) { Text(label) }
        Div({ style { property("font-size", "0.7rem"); property("color", "var(--rp-muted)") } }) { Text(sub) }
    }
}
